// ============================================================
// fondos/capacidad.js — ¿DÓNDE PUEDE IR UN FONDO ANIMADO, Y SE VE? (módulo PURO)
//
// Es la fuente única de una pregunta que antes contestaban seis listas sin dueño
// (`BG_ELIGIBLE_EXCLUDE`, `BG_COMPATIBILITY`, la línea del prompt, el cupo por página…),
// ninguna de las cuales miraba la VARIANTE ni los PROPS del bloque. Resultado, medido con el
// arnés A del plan `CLAUDE_RESUMEN_PLANES/2026-09-22_plan-motor-fondos.md` (§1.2):
//   · el panel ofrecía el fondo en un carrusel y la foto lo tapaba al 100 % (L6);
//   · la IA se lo asignaba a un `cta-bar`, que no emite <section>: nunca se pintaba y además
//     gastaba uno de los 4 del cupo de la página (L9);
//   · un hero con vídeo de fondo animaba un lienzo detrás del iframe, sin que se viera nada.
//
// TRES MODOS:
//   · 'seccion'    — el fondo va detrás del contenido de la sección. El caso normal.
//   · 'sobre-foto' — la sección tiene una FOTO de fondo: solo los fondos SUAVES y con la
//                    opacidad acotada, para que la foto se siga viendo (decisión del dueño Q2,
//                    22-09-2026). Los que cubren la sección no se ofrecen ni se pintan.
//   · 'no'         — no hay dónde pintarlo o no se vería: ni se ofrece ni se inyecta.
//
// Es una BLOCKLIST a propósito (gotcha 29 del CODEBASE_SUMMARY): un bloque nuevo hereda
// 'seccion' sin tocar nada. Lo que no puede heredar es justo lo excepcional.
//
// CONSUMIDORES (todos leen de aquí; ninguno lleva su propia lista):
//   · el botón del BG Studio (editor/tools.js) y el panel (editor/bg-studio.js, con el motivo);
//   · la inyección (fondos/inyectar.js), que comparten render.js y compileSiteHtml: lo que no
//     se puede ver no llega ni a crear su capa;
//   · el runtime (animations/fondos/efectiva.js::configEfectiva): la opacidad sobre una foto;
//   · el backend, por el build (`core_services/bg_meta.json` → `core_services/bg_meta.py`):
//     `BG_COMPATIBILITY` ⊆ capacidad, el cupo de fondos por página y el saneo de lo que asigna
//     la IA (`llm_sanitizers._modo_fondo`, espejo POR CASOS de `fondoDeBloque`, guardado por
//     `tests/test_fondos_capas.py`).
//
// MÓDULO PURO: ES2019 sin `?.` ni `??` y cero DOM; solo importa el catálogo (también puro). Lo
// importan el build (node 12) y los tests de node.
// ============================================================

import { FONDOS_SOBRE_FOTO_CATALOGO } from './catalogo.js';

export const MODO_SECCION = 'seccion';
export const MODO_SOBRE_FOTO = 'sobre-foto';
export const MODO_NO = 'no';

/**
 * Tipos que NUNCA llevan fondo, con el motivo que se le enseña al cliente.
 * header/footer/cta-bar no emiten <section> (no hay dónde colgar la capa); ai-custom es HTML
 * libre dentro de un iframe (el fondo quedaría detrás); imagen y carrusel son una foto a sangre
 * que cubre la sección entera (medido: 0-2 % visible en el carrusel).
 */
export const TIPOS_SIN_FONDO = {
    'header': 'La cabecera no es una sección: no hay dónde pintar un fondo animado.',
    'footer': 'El pie no es una sección: no hay dónde pintar un fondo animado.',
    'cta-bar': 'La barra de acción no es una sección: no hay dónde pintar un fondo animado.',
    'ai-custom': 'Este bloque vive dentro de su propio marco: el fondo animado quedaría detrás, sin verse.',
    'imagen': 'La foto ocupa la sección entera: el fondo animado quedaría debajo, sin verse.',
    'carrusel': 'Las fotos del carrusel ocupan la sección entera: el fondo animado quedaría debajo, sin verse.',
};

/** Los tipos de arriba, como lista (el nombre histórico que importa `editor/tools.js`). */
export const BG_ELIGIBLE_EXCLUDE = Object.keys(TIPOS_SIN_FONDO);

export const MOTIVO_VIDEO = 'El vídeo de fondo ocupa la sección entera: el fondo animado quedaría debajo, sin verse.';
export const MOTIVO_SOBRE_FOTO = 'Sobre una foto solo se ofrecen los fondos suaves, y con la opacidad acotada: los que cubren la sección taparían la foto.';

/**
 * Fondos que se admiten SOBRE UNA FOTO. Criterio MEDIDO (arnés B, 22-09-2026, columna «Foto:
 * tapada · detalle»): los que dejan la foto a la vista (≤ 1 % tapada, ≥ 89 % del detalle).
 * Fuera quedan los que la cubren: waves (35 % del detalle), aurora (79 %), smoke (10 %),
 * gradient-flow (20-31 %), matrix-rain (44 %) y mesh-gradient (45 %); y blobs-morph, que es de
 * la familia de manchas de color aunque deje el 95 %: pinta la foto de su tono.
 * La marca vive en el catálogo (`sobreFoto` de cada fondo, `fondos/catalogo.js`); aquí solo se
 * deriva, en el orden del panel.
 */
export const FONDOS_SOBRE_FOTO = FONDOS_SOBRE_FOTO_CATALOGO.slice();

/**
 * Tope de opacidad (0-100) de un fondo sobre una foto. Los suaves no tapan la foto ni al 100 %
 * (arnés B), así que el tope no está para salvar el contraste —eso lo hace el velo, que va
 * POR ENCIMA del fondo— sino para que el fondo nunca compita con la foto, que es la protagonista.
 */
export const OPACIDAD_MAX_SOBRE_FOTO = 80;

function _texto(v) {
    return typeof v === 'string' && v.trim() !== '';
}

/** ¿La sección lleva un vídeo de fondo? Espejo de `isVideo` en blocks/hero.js y blocks/video.js. */
function _hayVideo(p) {
    const v = p.video_url;
    return !!v && v !== 'placeholder';
}

/** ¿Hay una foto con la que rellenar el fondo? Mismo criterio que `llm_sanitizers._tiene_foto`. */
function _hayFoto(p) {
    return _texto(p.image_url) || _texto(p.image_keyword);
}

function _seccion() { return { modo: MODO_SECCION, motivo: '' }; }
function _sobreFoto() { return { modo: MODO_SOBRE_FOTO, motivo: MOTIVO_SOBRE_FOTO }; }
function _no(motivo) { return { modo: MODO_NO, motivo: motivo }; }

/**
 * LA pregunta: `{modo, motivo}` para un bloque `{type, props}`.
 *
 * Las condiciones de FOTO y VÍDEO son espejo de los RENDERERS, no de las reglas de contraste:
 *   · hero (blocks/hero.js): la v3 con foto o vídeo es un SPLIT —el medio va en una columna, no
 *     de fondo—; el resto de variantes (v1, v2, v4 y la v3 sin medio) pinta la foto como fondo
 *     de la sección si `use_image` y hay foto, y el vídeo como fondo si lo hay (el vídeo manda).
 *     Ojo: `utils/foto-fondo.js::pintaFotoDeFondo` responde OTRA pregunta (contra qué se mide el
 *     contraste del titular) y por eso deja fuera la v4, cuyo texto va en un panel de vidrio.
 *     Aquí la pregunta es qué hay DEBAJO del fondo animado, y en la v4 también es la foto.
 *   · banner (blocks/banner.js): solo la v2 lleva foto de fondo, y le basta con la foto.
 *   · video (blocks/video.js): con vídeo, el vídeo cubre la sección; sin él, `image_url` es una
 *     foto de fondo (y si tampoco hay, un degradado: sección normal).
 *
 * @param {{type: string, props?: Object}} block
 * @returns {{modo: 'seccion'|'sobre-foto'|'no', motivo: string}}
 */
export function fondoDeBloque(block) {
    const tipo = block && block.type;
    if (!tipo || typeof tipo !== 'string') return _no('Bloque desconocido.');
    if (Object.prototype.hasOwnProperty.call(TIPOS_SIN_FONDO, tipo)) return _no(TIPOS_SIN_FONDO[tipo]);

    const p = (block.props && typeof block.props === 'object') ? block.props : {};
    const variante = p.variant || 'v1';

    if (tipo === 'hero') {
        const video = _hayVideo(p);
        const split = variante === 'v3' && (!!p.use_image || video);
        if (!split) {
            if (video) return _no(MOTIVO_VIDEO);
            if (p.use_image && _hayFoto(p)) return _sobreFoto();
        }
        return _seccion();
    }
    if (tipo === 'banner') {
        return (variante === 'v2' && _hayFoto(p)) ? _sobreFoto() : _seccion();
    }
    if (tipo === 'video') {
        if (_hayVideo(p)) return _no(MOTIVO_VIDEO);
        return _texto(p.image_url) ? _sobreFoto() : _seccion();
    }
    return _seccion();
}

/** ¿Se puede poner ESTE fondo (`id` de BG_CATALOG) con esta capacidad? 'none' siempre se puede. */
export function fondoPermitido(capacidad, id) {
    if (!id || id === 'none') return true;
    if (!capacidad || capacidad.modo === MODO_NO) return false;
    if (capacidad.modo === MODO_SOBRE_FOTO) return FONDOS_SOBRE_FOTO.indexOf(id) !== -1;
    return true;
}

/** Tope de la opacidad del fondo (0-100) con esta capacidad. */
export function opacidadMaxima(capacidad) {
    return (capacidad && capacidad.modo === MODO_SOBRE_FOTO) ? OPACIDAD_MAX_SOBRE_FOTO : 100;
}

/** Lo que el build exporta al backend (`core_services/bg_meta.json`, clave `capacidad`). */
export function capacidadParaElBackend() {
    return {
        tipos_sin_fondo: BG_ELIGIBLE_EXCLUDE.slice(),
        fondos_sobre_foto: FONDOS_SOBRE_FOTO.slice(),
        opacidad_max_sobre_foto: OPACIDAD_MAX_SOBRE_FOTO,
    };
}
